/**
 * OnboardingFolder Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore.
 * Handles OnboardingFolder entity for organizing onboardings into folders.
 *
 * Sprint 1.1 - OnboardingFolder Types & Converter
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';

/**
 * OnboardingFolder interface
 * Represents a folder for organizing onboardings
 * Uses flat structure with parentFolderId for nested folders (optional future support)
 */
export interface OnboardingFolder {
  id: string;
  projectId: string;
  name: string;
  parentFolderId: string | null; // null = root level folder
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

/**
 * Firestore converter for OnboardingFolder
 * Handles Date↔Timestamp conversion and Client SDK signature compliance
 */
export const onboardingFolderConverter: FirestoreDataConverter<OnboardingFolder> = {
  /**
   * Convert OnboardingFolder to Firestore format
   * - Converts ISO date strings to Firestore Timestamps
   * - Removes id from data (stored in document metadata)
   */
  toFirestore(folder: OnboardingFolder): Record<string, unknown> {
    const { id, ...data } = folder;

    return {
      ...data,
      createdAt: Timestamp.fromDate(new Date(data.createdAt)),
      updatedAt: Timestamp.fromDate(new Date(data.updatedAt)),
    };
  },

  /**
   * Convert Firestore document to OnboardingFolder
   * - Converts Firestore Timestamps to ISO date strings
   * - Adds document id to returned object
   * - Uses Client SDK signature (snapshot, options?)
   */
  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): OnboardingFolder {
    const data = snapshot.data(options);

    return {
      id: snapshot.id,
      projectId: data.projectId,
      name: data.name,
      parentFolderId: data.parentFolderId ?? null,
      createdAt: data.createdAt?.toDate?.()
        ? data.createdAt.toDate().toISOString()
        : data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()
        ? data.updatedAt.toDate().toISOString()
        : data.updatedAt,
    };
  },
};
